/**
 * Review-queue API client + wire-contract types — #935 PR-D.
 *
 * Repointed from the archived review-queue surface to the
 * rebuilt backend's `/api/review-queue/*` (composition root:
 * `backend/src/contexts/review/http/routes.ts`). The new surface is
 * intentionally leaner:
 *
 *   GET  /api/review-queue                 list (inbox; open by default)
 *   POST /api/review-queue/{id}/resolve    resolve with a disposition option
 *   POST /api/review-queue/bulk-resolve    one disposition across many ids
 *
 * There is no `/count`, `/{id}` detail, or `/by-order` route on the new
 * backend — the inbox derives its badge from the list `total`, and the
 * per-row `resolveOptions` + `details_json` come inline on each item.
 *
 * `http.get` returns the UNWRAPPED inner `data` (#935-A), so the list
 * caller reads `.items` / `.total` directly. Item + result shapes are
 * consumed from the generated OpenAPI types (ADR-0006 — route is spec);
 * the `REVIEW_QUEUE_KIND` / `REVIEW_QUEUE_STATUS` maps are the closed-set
 * SOT the UI derives filter options + badge tables from (a typo fails
 * loud at the consumer's `Record` exhaustiveness check rather than
 * rendering a raw enum string).
 */

import { http } from '@/lib/api';
import { merchantDayStartUtc } from '@/lib/datetime';
import type { components } from '@/types/api.generated';
import { REVIEW_QUEUE_STATUS } from './review-queue.constants';
import type { ReviewQueueStatusFilter } from './review-queue.constants';

export type ReviewQueueItem = components['schemas']['ReviewQueueItem'];
export type ReviewQueueList = components['schemas']['ReviewQueueList'];
export type ReviewQueueResolveResult = components['schemas']['ReviewQueueResolveResult'];
export type ReviewQueueBulkResolveResult = components['schemas']['ReviewQueueBulkResolveResult'];

/**
 * The four closed sets moved to `./review-queue.constants.ts` in #1805 A3b so
 * the cell projections in `features/admin/widgets.tsx` can read them without
 * dragging `@/lib/api` into every test suite (see that file's header). They are
 * re-exported here so the `@/services/review-queue` import path is unchanged;
 * edit the constants module, not this file.
 */
export {
  POST_CUTOFF_CHANGE_TYPE,
  POST_DISPATCH_CANCELLATION_CAUSE,
  REVIEW_ERROR_KIND,
  REVIEW_QUEUE_KIND,
  REVIEW_QUEUE_STATUS,
  REVIEW_QUEUE_STATUS_FILTER,
} from './review-queue.constants';
export type {
  PostCutoffChangeType,
  PostDispatchCancellationCause,
  ReviewErrorKind,
  ReviewQueueKind,
  ReviewQueueStatus,
  ReviewQueueStatusFilter,
} from './review-queue.constants';

export interface ListParams {
  /**
   * `open` is the pending view. Passed EXPLICITLY by the inbox page (L22 —
   * an explicit param, not a hidden default filter); omitting it returns
   * the backend default (also open). `resolved` fetches only the closed
   * rows, and `all` applies no status predicate at all — the history tab's
   * ledger view (#1998).
   */
  status?: ReviewQueueStatusFilter;
  /** 1-based page number (shared list kernel). */
  page?: number;
  /**
   * Page size (shared list kernel). The backend clamps to floor 1 / ceiling
   * 100 (`reviewQueueListConfig` maxSize); an out-of-range value is clamped,
   * never rejected.
   */
  size?: number;
  /**
   * Kernel `<field>:<dir>` sort. Whitelisted backend-side against
   * `reviewQueueListConfig.sortable` (`REVIEW_QUEUE_SORTS` on this side), which
   * is what gates the clickable table headers; an out-of-set field 400s. Omit
   * to get the kernel default (`created_at:desc`).
   */
  sort?: string;
  /**
   * Shared list-query filters, pre-serialized to the `<field>=<op>:<value>`
   * wire form (via `serializeConditionsToParams`). Currently only `kind`
   * (eq/in) — the backend `reviewQueueListConfig` whitelist. Each entry is
   * appended as a query param; the backend validates fields/ops and 400s an
   * out-of-whitelist key.
   */
  filters?: Record<string, string>;
}

export interface ResolvePayload {
  resolution: string;
  note?: string;
}

export const reviewQueueAPI = {
  async list(params: ListParams = {}): Promise<ReviewQueueList> {
    const search = new URLSearchParams();
    // `status` stays an explicit param; `page` is a kernel reserved key; the
    // `filters` map carries the whitelisted `<field>=<op>:<value>` conditions.
    if (params.status) search.set('status', params.status);
    if (params.page != null) search.set('page', String(params.page));
    if (params.size != null) search.set('size', String(params.size));
    if (params.sort) search.set('sort', params.sort);
    for (const [field, value] of Object.entries(params.filters ?? {})) {
      search.set(field, value);
    }
    const qs = search.toString();
    return http.get<ReviewQueueList>(`/api/review-queue${qs ? `?${qs}` : ''}`);
  },

  async resolve(id: string, payload: ResolvePayload): Promise<ReviewQueueResolveResult> {
    return http.post<ReviewQueueResolveResult>(`/api/review-queue/${id}/resolve`, {
      resolution: payload.resolution,
      ...(payload.note != null ? { note: payload.note } : {}),
    });
  },

  async bulkResolve(
    ids: string[],
    resolution: string,
    note?: string,
  ): Promise<ReviewQueueBulkResolveResult> {
    return http.post<ReviewQueueBulkResolveResult>('/api/review-queue/bulk-resolve', {
      ids,
      resolution,
      ...(note != null ? { note } : {}),
    });
  },
};

/**
 * The status view + date bound that DEFINE "pending" — open AND raised today.
 *
 * It lives HERE, next to the client that sends it, because three surfaces ask
 * the same question: the inbox list, the count on the inbox's Pending tab, and
 * the review chip + KPI on the dispatch console (#1998, #2004). It started out
 * inside `features/admin/pages/reviewQueueResource.tsx`, which put the dispatch
 * console one deep cross-feature import away from the answer — so it did the
 * obvious thing instead and asked for every open row ever filed. The numbers
 * disagreed, and the chip that navigates to the Pending tab reported a bigger
 * backlog than the tab it opened.
 *
 * The day comes from the caller: a component passes `useToday()`, so the
 * bound — and the query key it rides in — moves at merchant midnight with
 * every list; a `queryFn` passes the day `todayInMerchantTz` reads at fetch
 * time.
 */
export function pendingViewParams(today: string): {
  status: typeof REVIEW_QUEUE_STATUS.OPEN;
  filters: Record<string, string>;
} {
  return {
    status: REVIEW_QUEUE_STATUS.OPEN,
    filters: { created_at: `from:${merchantDayStartUtc(today)}` },
  };
}
